import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, push } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { ClipboardList, Plus, X, AlertCircle, MessageCircle, ArrowLeft } from "lucide-react";

function RequestCard({ req }) {
  const waMsg = encodeURIComponent(
    `Hi! I have "${req.item}" that you're looking for. I saw your request on CampusSwap. Interested?`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-semibold text-gray-800">{req.item}</p>
          <p className="text-blue-600 font-bold text-sm mt-0.5">
            Budget: {req.maxPrice === 0 ? "Open / Negotiable" : `₹${req.maxPrice}`}
          </p>
          {req.description && <p className="text-gray-500 text-xs mt-1 line-clamp-2">{req.description}</p>}
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-50">
        <div>
          <p className="text-xs text-gray-500 font-medium">{req.postedByName}</p>
          <p className="text-xs text-gray-400">{req.college}</p>
        </div>
        {req.whatsapp && (
          <a href={`https://wa.me/91${req.whatsapp.replace(/\D/g, "")}?text=${waMsg}`}
            target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 bg-green-500 text-white text-xs px-3 py-1.5 rounded-xl font-semibold hover:bg-green-600">
            <MessageCircle size={13} /> I Have This!
          </a>
        )}
      </div>
    </div>
  );
}

export default function Requests() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ item: "", maxPrice: "", description: "", whatsapp: userProfile?.phone || "" });

  useEffect(() => { fetchRequests(); }, []);

  async function fetchRequests() {
    setLoading(true);
    try {
      const snap = await get(ref(db, "requests"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setRequests(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setRequests([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.item.trim()) return setError("Enter the item you need.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      await push(ref(db, "requests"), {
        item: form.item.trim(),
        maxPrice: Number(form.maxPrice) || 0,
        description: form.description.trim(),
        whatsapp: form.whatsapp.trim(),
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || "Student",
        college: userProfile?.college || "",
        createdAt: new Date().toISOString(),
      });
      setForm({ item: "", maxPrice: "", description: "", whatsapp: userProfile?.phone || "" });
      setShowForm(false);
      fetchRequests();
    } catch (err) { setError("Failed to post. Try again."); }
    setPosting(false);
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Request Board</h1>
              <p className="text-red-100 text-xs mt-0.5">Post what you need · Find what others want</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-red-600 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Post Request
          </button>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-3">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)
        ) : requests.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <ClipboardList size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No requests yet</p>
            <p className="text-gray-400 text-sm mt-1">Post what you're looking for!</p>
            <button onClick={() => setShowForm(true)} className="mt-4 bg-red-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Post a Request
            </button>
          </div>
        ) : requests.map(r => <RequestCard key={r.id} req={r} />)}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full">
            <div className="px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">What do you need?</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm"><AlertCircle size={16} />{error}</div>}
              <input name="item" value={form.item} onChange={handleChange} required
                placeholder="Item name (e.g. AutoCAD Textbook, Drawing Board)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-red-400" />
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
                <input name="maxPrice" type="number" min="0" value={form.maxPrice} onChange={handleChange}
                  placeholder="Max budget (0 = open to negotiate)"
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm focus:ring-2 focus:ring-red-400" />
              </div>
              <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                placeholder="Any specific requirements - edition, condition, etc."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-red-400 resize-none" />
              <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                placeholder="Your WhatsApp number *"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-red-400" />
              <button type="submit" disabled={posting}
                className="w-full bg-red-500 text-white py-3.5 rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50">
                {posting ? "Posting..." : "Post Request"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
